import { GUEST_OFFICE_NOT_OPEN_ERROR } from './guestRoomJoin'
import { isGuestMeetingUnavailableError } from './guestJoinFailure'

export type GuestFarewellReason =
  | 'ended'
  | 'abandoned'
  | 'removed'
  | 'already_connected'
  | 'join_failed'
  | 'office_not_open'

/** Set when the guest already uses Sevv in another company; read by GuestFarewellScreen. */
export const FAREWELL_SEVV_USER_PARAM = 'sevvUser'

/**
 * Set while Registration Mode is Closed (SC-443), so the farewell offers the waitlist instead of a
 * free trial. Only the flag travels: the waitlist address is read from the workspace settings, so
 * a crafted farewell link can never point a Sevv page somewhere else.
 */
export const FAREWELL_SIGNUP_CLOSED_PARAM = 'signupClosed'

/** The guest's meeting token, so a failed join can offer "Try again" back to the join page. */
export const FAREWELL_RETRY_TOKEN_PARAM = 'token'

/**
 * The farewell screen is a full page load that only sees URL params (the Redux guest state is
 * gone), so everything it shows has to travel in the URL built here.
 */
export function buildGuestFarewellUrl(opts: {
  reason: GuestFarewellReason
  companyName?: string | null
  hasSevvAccount?: boolean | null
  signupClosed?: boolean | null
  retryToken?: string | null
}): string {
  const params = new URLSearchParams({ reason: opts.reason })
  if (opts.companyName) params.set('company', opts.companyName)
  if (opts.hasSevvAccount) params.set(FAREWELL_SEVV_USER_PARAM, '1')
  if (opts.signupClosed) params.set(FAREWELL_SIGNUP_CLOSED_PARAM, '1')
  if (opts.retryToken) params.set(FAREWELL_RETRY_TOKEN_PARAM, opts.retryToken)
  return `/meeting/farewell?${params.toString()}`
}

/** The parts of the guest store's meeting info (from validate-token) the farewell uses. */
export type GuestFarewellMeetingInfo = {
  companyName?: string
  hasSevvAccount?: boolean
  signupClosed?: boolean
}

/** Farewell URL for the current guest session, from the meeting info in the guest store. */
export function guestFarewellUrlFromMeetingInfo(
  reason: GuestFarewellReason,
  meetingInfo: GuestFarewellMeetingInfo | null | undefined
): string {
  return buildGuestFarewellUrl({
    reason,
    companyName: meetingInfo?.companyName,
    hasSevvAccount: meetingInfo?.hasSevvAccount,
    signupClosed: meetingInfo?.signupClosed,
  })
}

/**
 * Which farewell to show when the guest's room join fails. Matches the errors the server's guest
 * auth throws (`GuestSessionService.validateMeetingToken`, `Wiggin.onAuth`); anything else is a
 * connection problem the guest can retry.
 */
export function guestJoinFailureReason(error: string | null | undefined): GuestFarewellReason {
  const message = (error || '').toLowerCase()
  if (message.includes('already connected')) return 'already_connected'
  if (message.includes('no longer on the invite list')) return 'removed'
  if (message.includes('meeting has been cancelled') || message.includes('meeting token has expired')) return 'ended'
  // SC-409: the host company is suspended or deleted. The guest gets the neutral "ended" page with
  // no retry; the server's transient "could not be joined right now" text falls through to
  // join_failed so the guest can try again.
  if (isGuestMeetingUnavailableError(error)) return 'ended'
  if (message.includes(GUEST_OFFICE_NOT_OPEN_ERROR.toLowerCase())) return 'office_not_open'
  return 'join_failed'
}

/** Reasons where opening the join link again can work, so the farewell offers "Try again". */
export function isRetryableGuestFarewell(reason: string): boolean {
  return reason === 'join_failed' || reason === 'office_not_open'
}

export function guestJoinFailureUrl(
  error: string | null | undefined,
  meetingInfo: GuestFarewellMeetingInfo | null | undefined,
  meetingToken: string | null | undefined
): string {
  const reason = guestJoinFailureReason(error)
  return buildGuestFarewellUrl({
    reason,
    companyName: meetingInfo?.companyName,
    hasSevvAccount: meetingInfo?.hasSevvAccount,
    signupClosed: meetingInfo?.signupClosed,
    retryToken: isRetryableGuestFarewell(reason) ? meetingToken : null,
  })
}
